import { useCallback } from 'react'
import { CustomFieldEntity, CustomFieldEntityItem } from '@cims-online/shared'
import {
  useCustomFieldCasEntityOptionsQuery,
  useCustomFieldCtsEntityOptionsQuery,
  useCustomFieldDsEntityOptionsQuery,
  useCustomFieldFdcEntityOptionsQuery,
} from 'schema'
import { DefaultQueryHookOptions } from 'utils/defaults'
import { useAnyTrue } from 'hooks/useAnyTrue'
import { useOptions } from '../../../hooks/useOptions'
import { createStrictContext } from 'utils/createContext'
import {
  CustomFieldEntityRecordSelectContext,
  CustomFieldEntityRecordSelectProviderProps,
  FromLabeledValuesValues,
} from './types'
import { SEPARATOR } from '../../types'
import { split, toLabeledValues } from './utils'
import { useProjectContext } from 'context/ProjectContext'

const { Provider, useContextFn } =
  createStrictContext<CustomFieldEntityRecordSelectContext>(
    'CustomFieldEntityRecordSelectContext'
  )

export const useCustomFieldEntityRecordSelectContext = useContextFn

const CustomFieldEntityRecordSelectProvider: React.FC<
  CustomFieldEntityRecordSelectProviderProps
> = ({ children, entities }) => {
  const { projectId } = useProjectContext()
  const defaults = {
    ...DefaultQueryHookOptions,
    variables: { globalId: projectId },
  }
  const In = useCallback<(entity: CustomFieldEntityItem) => boolean>(
    ({ id }) => (entities ? entities.some((entity) => entity.id === id) : true),
    [entities]
  )
  const cas = useCustomFieldCasEntityOptionsQuery({
    ...defaults,
    skip: !In(CustomFieldEntity.CAS),
  })
  const cts = useCustomFieldCtsEntityOptionsQuery({
    ...defaults,
    skip: !In(CustomFieldEntity.CTS),
  })
  const ds = useCustomFieldDsEntityOptionsQuery({
    ...defaults,
    skip: !In(CustomFieldEntity.DS),
  })
  const fdc = useCustomFieldFdcEntityOptionsQuery({
    ...defaults,
    skip: !In(CustomFieldEntity.FDC),
  })

  const casOptions = useOptions({
    items: cas.data?.constructionAdminSubmittals?.records,
    label: 'sharePointFolderName',
    value: 'id',
    sort: 'sharePointFolderName',
  })
  const ctsOptions = useOptions({
    items: cts.data?.concreteTestSets?.records,
    label: 'testSetId',
    value: 'globalId',
    sort: 'testSetId',
  })
  const dsOptions = useOptions({
    items: ds.data?.designSubmittals?.records,
    label: 'sharePointFolderName',
    value: 'id',
    sort: 'sharePointFolderName',
  })
  const fdcOptions = useOptions({
    items: fdc.data?.fieldDesignChanges?.records,
    label: 'number',
    value: 'id',
    sort: 'number',
  })

  const getOptions = useCallback<
    CustomFieldEntityRecordSelectContext['getOptions']
  >(
    (entity) => {
      let options
      if (entity === CustomFieldEntity.CAS) options = casOptions
      else if (entity === CustomFieldEntity.CTS) options = ctsOptions
      else if (entity === CustomFieldEntity.DS) options = dsOptions
      else if (entity === CustomFieldEntity.FDC) options = fdcOptions
      return options ?? []
    },
    [casOptions, ctsOptions, dsOptions, fdcOptions]
  )

  const [loading] = useAnyTrue([
    cas.loading,
    cts.loading,
    ds.loading,
    fdc.loading,
  ])

  const options = useOptions({
    items: CustomFieldEntity.values.filter(({ id }) => {
      if (entities) return entities.some((entity) => entity.id === id)
      return true
    }),
    type: 'custom',
    custom: (entity) => ({
      label: entity.type,
      value: entity.id,
      options: getOptions(entity).map(({ value, ...option }) => ({
        value: `${entity.id}${SEPARATOR}${value}`,
        ...option,
      })),
    }),
    sort: 'type',
  })

  return (
    <Provider
      value={{
        getOptions,
        loading,
        options,
        toLabeledValues,
        toLabeledValue: (record) =>
          record ? toLabeledValues([record])[0] : undefined,
        fromLabeledValues: (values) =>
          (values ?? []).reduce<FromLabeledValuesValues>(
            (values, { value }) => {
              const {
                constructionAdminSubmittalId,
                concreteTestSetGlobalId,
                designSubmittalId,
                fieldDesignChangeId,
              } = split(value) ?? {}
              if (constructionAdminSubmittalId)
                values.constructionAdminSubmittalIds.push(
                  constructionAdminSubmittalId
                )
              else if (concreteTestSetGlobalId)
                values.concreteTestSetGlobalIds.push(concreteTestSetGlobalId)
              else if (designSubmittalId)
                values.designSubmittalIds.push(designSubmittalId)
              else if (fieldDesignChangeId)
                values.fieldDesignChangeIds.push(fieldDesignChangeId)
              return values
            },
            {
              constructionAdminSubmittalIds: [],
              concreteTestSetGlobalIds: [],
              designSubmittalIds: [],
              fieldDesignChangeIds: [],
            }
          ),
        fromLabeledValue: (value) => {
          const {
            constructionAdminSubmittalId,
            concreteTestSetGlobalId,
            designSubmittalId,
            fieldDesignChangeId,
          } = split(value?.value) ?? {}
          if (constructionAdminSubmittalId)
            return { constructionAdminSubmittalId }
          else if (concreteTestSetGlobalId) return { concreteTestSetGlobalId }
          else if (designSubmittalId) return { designSubmittalId }
          else if (fieldDesignChangeId) return { fieldDesignChangeId }
          return undefined
        },
      }}
    >
      {children}
    </Provider>
  )
}

export default CustomFieldEntityRecordSelectProvider
